/**
 * @module features/devices/api/useDeviceMutations
 * @description Mutation hooks. Every mutation invalidates list, detail and stats
 * queries so the UI never shows stale fleet data.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DeviceUpdateInput } from "../types";
import {
  createDevice,
  decommissionDevice,
  deviceKeys,
  reactivateDevice,
  updateDevice,
} from "./devices-api";

function useInvalidateDevices() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: deviceKeys.all });
}

export function useCreateDevice() {
  const invalidate = useInvalidateDevices();
  return useMutation({ mutationFn: createDevice, onSuccess: invalidate });
}

export function useUpdateDevice() {
  const invalidate = useInvalidateDevices();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: DeviceUpdateInput }) =>
      updateDevice(id, input),
    onSuccess: invalidate,
  });
}

export function useDecommissionDevice() {
  const invalidate = useInvalidateDevices();
  return useMutation({ mutationFn: (id: string) => decommissionDevice(id), onSuccess: invalidate });
}

export function useReactivateDevice() {
  const invalidate = useInvalidateDevices();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status?: "PROVISIONED" | "ACTIVE" }) =>
      reactivateDevice(id, status),
    onSuccess: invalidate,
  });
}
